import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CaUpload from './ca_upload';
import CaCopy from './ca_copy';
import ManagedInput from 'components/form/managed_input';
import Toggle from 'components/toggle';

import { faFilePdf, faFileWord } from '@fortawesome/pro-solid-svg-icons';
import { Card, Col, Form, Row } from 'react-bootstrap-5';

const PanelHeader = ({ allowMultiple, checked, title, onChange }) => (
  <Card.Header>
    <Row className="align-items-center">
      <Col>
        <div className="fw-bold">{title}</div>
      </Col>
      <Col xs="auto">
        {allowMultiple ? (
          <Toggle
            checked={checked}
            name={title}
            showText={false}
            theme="ds"
            onChange={() => onChange(!checked)}
          />
        ) : (
          <Form.Check
            checked={checked}
            className="form-check-input-lg"
            type="radio"
            onClick={() => onChange(!checked)}
          />
        )}
      </Col>
    </Row>
  </Card.Header>
);

PanelHeader.propTypes = {
  allowMultiple: PropTypes.bool.isRequired,
  checked: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};
export default function ConfidentialityAgreementForm({
  allowDigitalSignPdfCas,
  allowMultipleCasPerRole,
  caData,
  caOptions,
  caUrl,
  roleLabel,
  show,
  standardText,
  onCaTypeChange,
  onCaUpload,
  onSimpleFieldChange
}) {
  if (!show) return null;
  // customCaEnabled is a boolean used to determine if the Digital Sign area should show standard CA or custom
  const [customCaEnabled, setCustomCaEnabled] = useState(caData.caTypeCustomText);

  useEffect(() => {
    // This hook allows the user to toggle between standard and custom CA types without changing the actual caType
    if (customCaEnabled && caData.caTypeStandard) {
      onCaTypeChange('caTypeCustomText', true);
    } else if (!customCaEnabled && caData.caTypeCustomText) {
      onCaTypeChange('caTypeStandard', true);
    }
  }, [customCaEnabled]);

  function digitalSignUploadZone() {
    if (!allowDigitalSignPdfCas) return null;

    const fileRemoved = () => onCaUpload({
      digitalSignUploadId: null, digitalSignFileFileName: null, digitalSignFileUpdatedAt: ''
    });
    const onFileUploaded = (upload) => {
      const dataToPass = {
        digitalSignUploadId: upload.id,
        digitalSignFileFileName: upload.file_name,
        digitalSignFileUpdatedAt: 'Just now'
      };
      onCaUpload(dataToPass);
    };

    return (
      <div className="mt-4">
        <CaUpload
          acceptedFileTypes={['application/pdf']}
          caUrl={caUrl + '?file=digital_sign_file'}
          icon={faFilePdf}
          key="digital_upload_pdf"
          uploadedAgo={caData.digitalSignFileUpdatedAt}
          uploadedFile={caData.digitalSignFileFileName}
          onFileRemove={fileRemoved}
          onFileUploaded={onFileUploaded}
        />
      </div>
    );
  }

  const findCaOption = (key) => {
    return caOptions.find(option => option[1] === key);
  };

  function renderTextCaCopy() {
    if (!customCaEnabled) return (
      <CaCopy
        key="standard"
        readOnly={true}
        text={standardText}
      />
    );

    const onCopyClick = () => onSimpleFieldChange('text', standardText);
    const onTextChange = newValue => onSimpleFieldChange('text', newValue);
    return (
      <React.Fragment>
        <CaCopy
          key="custom_text"
          readOnly={false}
          text={caData.text}
          onCopy={onCopyClick}
          onTextChange={onTextChange}
        />
        {digitalSignUploadZone()}
      </React.Fragment>
    );
  }

  const renderDigitalSignPanel = () => {
    const handleDigitalSignCheckToggle = (val) => {
      if (val) return onCaTypeChange('caTypeCustomText', true);

      if (caData.caTypeStandard) onCaTypeChange('caTypeStandard', false);
      if (caData.caTypeCustomText) onCaTypeChange('caTypeCustomText', false);
    };

    return (
      <Card>
        <PanelHeader
          allowMultiple={allowMultipleCasPerRole}
          checked={caData.caTypeCustomText || caData.caTypeStandard}
          title="Digital Sign CA"
          onChange={val => handleDigitalSignCheckToggle(val)}
        />
        <Card.Body>
          {renderTextCaCopy()}
          <div>
            <ManagedInput
              className="mt-3 form-control"
              id="ca_type_selection"
              type="select"
              value={customCaEnabled ? 'custom_text' : 'standard'}
              onValueChange={value => setCustomCaEnabled(value === 'custom_text')}
            >
              <option value="standard">Buildout Default</option>
              <option value="custom_text">Custom CA</option>
            </ManagedInput>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderPdfCAPanel = () => {
    if (!findCaOption('custom_pdf')) return null;

    const fileRemoved = () => onCaUpload({ uploadId: null, fileFileName: null, fileUpdatedAt: '' });
    const onFileUploaded = (upload) => {
      const dataToPass = { uploadId: upload.id, fileFileName: upload.file_name, fileUpdatedAt: 'Just now' };
      onCaUpload(dataToPass);
    };

    return (
      <Card className="flex-grow-1">
        <PanelHeader
          allowMultiple={allowMultipleCasPerRole}
          checked={caData.caTypeCustomPdf}
          title="PDF CA"
          onChange={val => onCaTypeChange('caTypeCustomPdf', val)}
        />
        <Card.Body>
          <CaUpload
            acceptedFileTypes={['application/pdf']}
            caUrl={caUrl + '?file=file'}
            icon={faFilePdf}
            key="custom_pdf"
            uploadedAgo={caData.fileUpdatedAt}
            uploadedFile={caData.fileFileName}
            onFileRemove={fileRemoved}
            onFileUploaded={onFileUploaded}
          />
        </Card.Body>
      </Card>
    );
  };

  const renderWordCAPanel = () => {
    if (!findCaOption('custom_word')) return null;

    const fileRemoved = () => onCaUpload({ wordUploadId: null, wordFileFileName: null, wordFileUpdatedAt: '' });
    const onFileUploaded = (upload) => {
      const dataToPass = {
        wordUploadId: upload.id,
        wordFileFileName: upload.file_name,
        wordFileUpdatedAt: 'Just now'
      };
      onCaUpload(dataToPass);
    };

    return (
      <Card className="flex-grow-1">
        <PanelHeader
          allowMultiple={allowMultipleCasPerRole}
          checked={caData.caTypeCustomWord}
          title="Word CA"
          onChange={val => onCaTypeChange('caTypeCustomWord', val)}
        />
        <Card.Body>
          <CaUpload
            acceptedFileTypes={['application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
            caUrl={caUrl + '?file=word_file'}
            icon={faFileWord}
            key="custom_word"
            uploadedAgo={caData.wordFileUpdatedAt}
            uploadedFile={caData.wordFileFileName}
            onFileRemove={fileRemoved}
            onFileUploaded={onFileUploaded}
          />
        </Card.Body>
      </Card>
    );
  };

  const showSecondColumn = useMemo(() => {
    return caOptions.some(option => ['custom_pdf', 'custom_word'].includes(option[1]));
  }, [caOptions]);

  return (
    <Card>
      <Card.Header>
        <div className="fs-large fw-semibold">{roleLabel} Confidentiality Agreement</div>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col>{renderDigitalSignPanel()}</Col>
          {showSecondColumn && (
            <Col className="d-flex gap-6 flex-column">
              {renderPdfCAPanel()}
              {renderWordCAPanel()}
            </Col>
          )}
        </Row>

      </Card.Body>
    </Card>
  );
}

export const CA_DATA_SHAPE = {
  id: PropTypes.number.isRequired,
  caType: PropTypes.string,
  digitalSignFileFileName: PropTypes.string,
  digitalSignFileUpdatedAt: PropTypes.string,
  text: PropTypes.string,
  fileFileName: PropTypes.string,
  fileUpdatedAt: PropTypes.string,
  wordFileFileName: PropTypes.string,
  wordFileUpdatedAt: PropTypes.string
};

ConfidentialityAgreementForm.propTypes = {
  allowDigitalSignPdfCas: PropTypes.bool.isRequired,
  allowMultipleCasPerRole: PropTypes.bool.isRequired,
  caData: PropTypes.shape(CA_DATA_SHAPE),
  caOptions: PropTypes.arrayOf(PropTypes.array).isRequired,
  caUrl: PropTypes.string.isRequired,
  roleLabel: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  standardText: PropTypes.string.isRequired,
  onCaTypeChange: PropTypes.func.isRequired,
  onCaUpload: PropTypes.func.isRequired,
  onSimpleFieldChange: PropTypes.func.isRequired
};
